import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { usePlayerTrackInfo } from '@components/AudioPlayer/usePlayerTrackInfo';
import { prefetchAudioFile } from '@store/audio/AudioFetcher';

export const usePrefetchAudioOnDelayedHover = ({
  hoverRef,
  duration,
  trackId,
}: {
  hoverRef: RefObject<HTMLButtonElement>;
  duration: number;
  trackId: string | null;
}) => {
  const hoverStartTimeRef = useRef<ReturnType<typeof setTimeout>>();
  const [shouldFetch, setShouldFetch] = useState(false);

  const playerTrackInfo = usePlayerTrackInfo({
    trackId,
    shouldFetch,
  });

  const { trackAudioUrl } = playerTrackInfo;

  useEffect(() => {
    const handleMouseEnter = () => {
      hoverStartTimeRef.current = setTimeout(() => {
        setShouldFetch(true);
      }, duration);
    };

    const handleMouseLeave = () => {
      clearTimeout(hoverStartTimeRef.current);
    };

    const element = hoverRef.current;

    if (element) {
      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (element) {
        element.removeEventListener('mouseenter', handleMouseEnter);
        element.removeEventListener('mouseleave', handleMouseLeave);
      }
      clearTimeout(hoverStartTimeRef.current);
    };
  }, [hoverRef, duration]);

  useEffect(() => {
    if (!trackAudioUrl || !shouldFetch) return;

    prefetchAudioFile({
      url: trackAudioUrl,
    });
  }, [trackAudioUrl, shouldFetch, trackId]);
};
